import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Table } from 'reactstrap';

import ISearchedPosition from 'api/interfaces/offers/ISearchedPosition';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import getContributionMarginRatio from 'services/products/calculations/getContributionMarginRatio';
import getExpensePerUnit from 'services/products/calculations/getExpensePerUnit';
import getInstallationSellingPrice from 'services/products/calculations/getInstallationSellingPrice';
import getInstallationTotalExpense from 'services/products/calculations/getInstallationTotalExpense';
import getSellingPricePerUnit from 'services/products/calculations/getSellingPricePerUnit';
import getSubproductSellingPrice from 'services/products/calculations/getSubproductSellingPrice';
import getSubproductTotalExpense from 'services/products/calculations/getSubproductTotalExpense';
import translations from 'services/translations/translations';

/**
 * The calculation of a position found by the position search, before it is copied: the
 * materials and installations of its calculation versions (the primary first) with their
 * quantities, costs and selling prices for one unit of the position, and the totals. The
 * buttons below go back to the results or copy the position to the offer.
 */
const PositionPreview = ({
  copyLabel,
  copying,
  onBack,
  onCopy,
  position,
}: {
  copyLabel: string;
  copying: boolean;
  onBack: () => void;
  onCopy: () => void;
  position: ISearchedPosition;
}): JSX.Element => {
  const { t } = useTranslation([
    translations.offers,
    translations.products,
    translations.common,
  ]);

  // The primary version first, then the others in their order
  const versions = [
    ...position.versions.filter((version) => version.isPrimary),
    ...position.versions.filter((version) => !version.isPrimary),
  ];
  const [versionIndex, setVersionIndex] = useState(0);
  const version = versions[versionIndex];

  const unitText = (unit: null | string) =>
    unit ? t(`${translations.common}:units:${unit}`) : '';
  const money = (value: number) => `${formatGroupedNumber(value)} €`;
  const amount = (quantity: number, unit: null | string) =>
    `${formatGroupedNumber(quantity, 2, 0)} ${unitText(unit)}`.trim();

  const quantity = position.quantity ?? 0;
  const sellingPerUnit = version
    ? getSellingPricePerUnit(version.installations, version.subproducts)
    : 0;
  const expensePerUnit = version
    ? getExpensePerUnit(version.installations, version.subproducts)
    : 0;
  const marginRatio = version
    ? getContributionMarginRatio(version.installations, version.subproducts)
    : 0;

  return (
    <div
      aria-labelledby="position-preview-title"
      className="position-preview"
      role="dialog"
    >
      <header>
        <h3 id="position-preview-title">
          {`${position.position} ${position.name}`}
        </h3>
        <p>
          {position.targetName}
          {position.revisionNumber > 1 ? ` (v${position.revisionNumber})` : ''}
          {position.organizationUnit ? ` · ${position.organizationUnit}` : ''}
          {` · ${amount(quantity, position.unit)}`}
        </p>
      </header>

      <div className="position-preview-body">
        {/* The calculation versions, the primary first */}
        {versions.length > 1 ? (
          <div className="position-preview-versions" role="tablist">
            {versions.map((choice, index) => (
              <button
                aria-selected={index === versionIndex}
                className={index === versionIndex ? 'active' : undefined}
                key={`${choice.name}-${index}`}
                onClick={() => setVersionIndex(index)}
                role="tab"
                type="button"
              >
                {choice.name}
                {choice.isPrimary
                  ? ` (${t(`${translations.offers}:positionSearch.Primary`)})`
                  : ''}
              </button>
            ))}
          </div>
        ) : null}

        {version === undefined ? (
          <p>{t(`${translations.offers}:positionSearch.No calculation`)}</p>
        ) : (
          <>
            {/* For one unit of the position, as in the calculation window */}
            <p className="position-preview-per-unit">
              {t(`${translations.offers}:positionSearch.Per unit`, {
                unit: unitText(position.unit),
              })}
            </p>
            <Table className="position-preview-table" size="sm">
              <thead>
                <tr>
                  <th>{t(`${translations.offers}:positionSearch.Row`)}</th>
                  <th className="numeric">
                    {t(`${translations.common}:Quantity`)}
                  </th>
                  <th className="numeric">
                    {t(`${translations.offers}:Expenses total`)}
                  </th>
                  <th className="numeric">
                    {t(`${translations.offers}:Selling price`)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {version.subproducts.length > 0 ? (
                  <tr className="position-preview-group">
                    <td colSpan={4}>
                      {t(`${translations.offers}:positionSearch.Materials`)}
                    </td>
                  </tr>
                ) : null}
                {version.subproducts.map((row, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={`subproduct-${index}`}>
                    <td>{row.name || '–'}</td>
                    <td className="numeric">
                      {amount(row.quantity ?? 0, row.quantityUnit)}
                    </td>
                    <td className="numeric">
                      {money(getSubproductTotalExpense(row))}
                    </td>
                    <td className="numeric">
                      {money(getSubproductSellingPrice(row))}
                    </td>
                  </tr>
                ))}
                {version.installations.length > 0 ? (
                  <tr className="position-preview-group">
                    <td colSpan={4}>
                      {t(`${translations.offers}:positionSearch.Installations`)}
                    </td>
                  </tr>
                ) : null}
                {version.installations.map((row, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={`installation-${index}`}>
                    <td>{row.name || row.category || '–'}</td>
                    <td className="numeric">
                      {amount(row.quantity ?? 0, row.quantityUnit)}
                    </td>
                    <td className="numeric">
                      {money(getInstallationTotalExpense(row))}
                    </td>
                    <td className="numeric">
                      {money(getInstallationSellingPrice(row))}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>
                    {`${t(
                      `${translations.offers}:positionSearch.Per unit total`
                    )} / ${unitText(position.unit)}`}
                  </td>
                  <td />
                  <td className="numeric">{money(expensePerUnit)}</td>
                  <td className="numeric">{money(sellingPerUnit)}</td>
                </tr>
              </tfoot>
            </Table>

            {/* The totals of the whole position */}
            <dl className="position-preview-totals">
              <div>
                <dt>{t(`${translations.offers}:Selling price`)}</dt>
                <dd>{money(sellingPerUnit * quantity)}</dd>
              </div>
              <div>
                <dt>{t(`${translations.offers}:Expenses total`)}</dt>
                <dd>{money(expensePerUnit * quantity)}</dd>
              </div>
              <div>
                <dt>{`${t(
                  `${translations.offers}:Contribution margin`
                )} €`}</dt>
                <dd>{money((sellingPerUnit - expensePerUnit) * quantity)}</dd>
              </div>
              <div>
                <dt>{`${t(
                  `${translations.offers}:Contribution margin`
                )} %`}</dt>
                <dd>{`${formatGroupedNumber(marginRatio)} %`}</dd>
              </div>
            </dl>
          </>
        )}
      </div>

      <footer>
        <Button className="modal-button" onClick={onBack}>
          {t(`${translations.offers}:positionSearch.Back`)}
        </Button>
        <Button className="modal-button" disabled={copying} onClick={onCopy}>
          {copyLabel}
        </Button>
      </footer>
    </div>
  );
};

export default PositionPreview;
